import React from 'react';
import './LobbyDesktop.css';
import LobbyOwnCard from './LobbyOwnCard';
import LobbyOpponentCard from './LobbyOpponentCard';
import LobbyControlBar from './LobbyControlBar';
import {getOpponentsGridLayout} from './getOpponentsGridLayout';
import {getLobbySubtitle, getLobbyTitle, LOBBY_MAX_PLAYERS} from '../lobbyHeading';

// Выданные фракции рейтингового лобби видны не сразу: в ручном лобби админа
// — после «Начать матч», в ИИ-лобби — когда сервер откроет баны
const getHiddenFactionsText = (isAiLobby) => (isAiLobby
  ? 'Фракции откроются перед стартом матча'
  : 'Фракции откроются, когда создатель начнет матч');

/**
 * Свободное место обычного лобби (игроков меньше шести). У создателя-одиночки
 * в нем — та же плашка «игроки ищут лобби», что на телефоне.
 */
const LobbyPlaceholderCard = ({searchingHint}) => (
  <div className="lobby-opponent-card lobby-opponent-card_placeholder">
    <div className="lobby-opponent-card_placeholder-title">Свободное место</div>
    {searchingHint && (
      <div className="lobby-desktop-search-hint">
        <span>Игроков сейчас ищет лобби:</span>
        <span className="text-attention">{searchingHint.count}</span>
        <span>Среднее время присоединения:</span>
        <span className="text-attention">{searchingHint.avgSeconds} сек</span>
      </div>
    )}
  </div>
);

/**
 * Десктопная раскладка лобби (ширина от 1024 px): шапка с режимом, слева —
 * своя крупная карточка со встроенным выбором фракции, справа — сетка
 * соперников, внизу — полоса всех кнопок управления и отсчет фазы (рядом с
 * кнопкой, которой фазу закрывают). Вся
 * логика, состояние сокета и обработчики — в LobbyPage; здесь только
 * раскладка и локальное состояние вида (карусель/все фракции).
 *
 * Помещается в экран без прокрутки (1366×768, 1024×700): высота — вся
 * видимая, внутренние области прокручиваются сами (см. LobbyDesktop.css).
 *
 * children — модалки и оверлеи лобби (те же, что у мобильной раскладки).
 */
const LobbyDesktop = ({
  lobby, players, networkId, actingPlayer, auth, isCreator, isAiLobby,
  countdown, banView, selectView, ownFactions, onBanVoteClick, onOpenFactionCard, onKickPlayer, searchingHint, controls,
  children,
}) => {
  const opponents = actingPlayer ? players.filter((player) => player.id !== networkId) : players;
  const hiddenFactionsText = getHiddenFactionsText(isAiLobby);
  const isPlaceholderVisible = !lobby.isRanked && players.length < LOBBY_MAX_PLAYERS;
  const cardsCount = opponents.length + (isPlaceholderVisible ? 1 : 0);
  const layout = getOpponentsGridLayout(cardsCount);

  return (
    <div className="lobby-desktop">
      <header className="lobby-desktop_header">
        <div className="lobby-desktop_heading">
          <h1 className="lobby-desktop_title">{getLobbyTitle(lobby, isAiLobby)}</h1>
          <div className="lobby-desktop_subtitle">{getLobbySubtitle(lobby, players.length)}</div>
        </div>
      </header>

      <div className="lobby-desktop_main">
        <div className="lobby-desktop_own">
          {actingPlayer ? (
            <LobbyOwnCard
              lobby={lobby}
              actingPlayer={actingPlayer}
              auth={auth}
              banView={banView}
              selectView={selectView}
              ownFactions={ownFactions}
              onOpenFactionCard={onOpenFactionCard}
              hiddenFactionsText={hiddenFactionsText}
            />
          ) : (
            // Зритель (ИИ-лобби, мест нет): своей карточки нет, все игроки — в сетке
            <div className="lobby-desktop_spectator">Вы зритель</div>
          )}
        </div>

        <div className="lobby-desktop_opponents" data-layout={layout}>
          {opponents.map((player) => (
            <LobbyOpponentCard
              key={player.id}
              player={player}
              lobby={lobby}
              isKickVisible={isCreator && player.id !== networkId}
              onKick={onKickPlayer}
              banView={banView}
              selectView={selectView}
              onBanVoteClick={onBanVoteClick}
              onOpenFactionCard={onOpenFactionCard}
              hiddenFactionsText={hiddenFactionsText}
            />
          ))}
          {isPlaceholderVisible && <LobbyPlaceholderCard searchingHint={searchingHint}/>}
        </div>
      </div>

      <LobbyControlBar
        lobby={lobby}
        isCreator={isCreator}
        controls={controls}
        banView={banView}
        selectView={selectView}
        countdown={countdown}
      />

      {children}
    </div>
  );
};

export default LobbyDesktop;
